// Local, on-device reading reminders. No backend, zero cost.
// 1) Inactivity nudge: if it's been ~24h since the last read.
// 2) Streak-at-risk nudge: late in the day, on a live streak, but not read yet today.
// Primary path: checks whenever the app opens / regains focus.
// Progressive enhancement: Periodic Background Sync (installed PWAs) lets the
// service worker nudge even when the app is closed.
import { getLast, getStreak } from "./storage";
import { idbSet } from "./idb";
import { getVOTD } from "../api";

const DAY = 24 * 60 * 60 * 1000;
const NUDGE_KEY = "fw_last_reminder";
const STREAK_NUDGE_KEY = "fw_last_streak_nudge";
const VOTD_NUDGE_KEY = "fw_last_votd_nudge";
const VOTD_CACHE_KEY = "fw_votd_cache";
const STREAK_RISK_HOUR = 19; // start reminding about the streak from 7pm
const VOTD_MORNING_START = 6;  // morning verse window: 6am–noon
const VOTD_MORNING_END = 12;

const todayStr = () => new Date().toISOString().slice(0, 10);
const dayStr = (ms) => new Date(ms).toISOString().slice(0, 10);

export function notificationsSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

export function notificationPermission() {
  return notificationsSupported() ? Notification.permission : "unsupported";
}

export async function enableReminders() {
  if (!notificationsSupported()) return false;
  if (Notification.permission === "granted") {
    await registerPeriodicSync();
    return true;
  }
  if (Notification.permission === "denied") return false;
  const res = await Notification.requestPermission();
  if (res === "granted") {
    await registerPeriodicSync();
    runReminderChecks();
    return true;
  }
  return false;
}

function notify(title, opts) {
  if (navigator.serviceWorker?.ready) {
    navigator.serviceWorker.ready
      .then((reg) => reg.showNotification(title, opts))
      .catch(() => new Notification(title, opts));
  } else {
    new Notification(title, opts);
  }
}

// Cache today's Verse of the Day so both the app and the (closed-app) service
// worker can show it in the morning notification.
export async function cacheVotd() {
  try {
    const d = await getVOTD();
    if (d && d.verse_text) {
      const v = { date: todayStr(), text: d.verse_text, ref: d.verse_ref };
      localStorage.setItem(VOTD_CACHE_KEY, JSON.stringify(v));
      idbSet("votd", v).catch(() => {});
    }
  } catch {
    /* offline — keep any previous cache */
  }
}

// Morning nudge with today's verse. Fires once per day in the morning window.
export function checkVotdReminder() {
  if (notificationPermission() !== "granted") return false;
  const h = new Date().getHours();
  if (h < VOTD_MORNING_START || h >= VOTD_MORNING_END) return false;
  const today = todayStr();
  const lastNudge = Number(localStorage.getItem(VOTD_NUDGE_KEY) || 0);
  if (dayStr(lastNudge) === today) return false;

  let cache = null;
  try { cache = JSON.parse(localStorage.getItem(VOTD_CACHE_KEY) || "null"); } catch { cache = null; }
  const body = cache?.text
    ? `\u201C${cache.text.length > 140 ? `${cache.text.slice(0, 140)}\u2026` : cache.text}\u201D \u2014 ${cache.ref}`
    : "Today's verse is ready. Tap to read and reflect.";

  localStorage.setItem(VOTD_NUDGE_KEY, String(Date.now()));
  notify("Verse of the Day \uD83C\uDF05", {
    body,
    icon: "/icon.svg",
    badge: "/icon.svg",
    tag: "verseflow-votd",
    data: { url: "/verse" },
  });
  return true;
}

// Late in the day, still on a streak, but haven't read today → offer to keep it.
export function checkStreakReminder() {
  if (notificationPermission() !== "granted") return false;
  const s = getStreak();
  if (!s.count || s.count < 1) return false;
  const today = todayStr();
  const yesterday = dayStr(Date.now() - DAY);
  if (s.last === today) return false;        // already read today — streak safe
  if (s.last !== yesterday) return false;    // streak already lapsed — not "on a streak"
  if (new Date().getHours() < STREAK_RISK_HOUR) return false; // wait until evening

  const lastNudge = Number(localStorage.getItem(STREAK_NUDGE_KEY) || 0);
  if (dayStr(lastNudge) === today) return false; // once per day
  localStorage.setItem(STREAK_NUDGE_KEY, String(Date.now()));

  const last = getLast();
  notify("Keep your streak alive 🔥", {
    body: `You're on a ${s.count}-day reading streak. Read a chapter before midnight to keep it going!`,
    icon: "/icon.svg",
    badge: "/icon.svg",
    tag: "verseflow-streak",
    data: { url: last ? `/read/${last.book}/${last.chapter}` : "/" },
  });
  return true;
}

// ~24h since the last time they read.
export function checkInactivityReminder() {
  if (notificationPermission() !== "granted") return false;
  const last = getLast();
  if (!last?.at) return false;
  if (Date.now() - last.at < DAY) return false;
  const lastNudge = Number(localStorage.getItem(NUDGE_KEY) || 0);
  if (Date.now() - lastNudge < DAY) return false; // don't nag more than once a day
  localStorage.setItem(NUDGE_KEY, String(Date.now()));

  notify("Clarity Bible — time to read 📖", {
    body: `It's been a day since ${last.bookName} ${last.chapter}. A few quiet minutes in the Word are waiting for you.`,
    icon: "/icon.svg",
    badge: "/icon.svg",
    tag: "verseflow-inactivity",
    data: { url: `/read/${last.book}/${last.chapter}` },
  });
  return true;
}

// VOTD (morning) → streak (evening) → inactivity. Only one fires per check.
export function runReminderChecks() {
  if (checkVotdReminder()) return;
  if (checkStreakReminder()) return;
  checkInactivityReminder();
}

export function startReminderWatcher() {
  if (!notificationsSupported()) return;
  idbSet("streak", getStreak()).catch(() => {}); // mirror for the closed-app SW path
  cacheVotd();                                    // refresh today's verse for the morning nudge
  runReminderChecks();
  setInterval(runReminderChecks, 30 * 60 * 1000);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") { cacheVotd(); runReminderChecks(); }
  });
  registerPeriodicSync();
}

async function registerPeriodicSync() {
  try {
    if (notificationPermission() !== "granted") return;
    const reg = await navigator.serviceWorker?.ready;
    if (!reg || !("periodicSync" in reg)) return;
    const status = await navigator.permissions.query({
      name: "periodic-background-sync",
    });
    if (status.state === "granted") {
      await reg.periodicSync.register("verseflow-reminder", {
        minInterval: 12 * 60 * 60 * 1000,
      });
    }
  } catch {
    /* unsupported — the on-open checks still cover this */
  }
}
